import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../../components/dashboard/DashboardLayout";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../../components/dashboard/DashboardStates";
import { useToast } from "../../../context/ToastContext";
import * as pagesApi from "../../../api/pagesApi";
import { getApiErrorMessage } from "../../../utils/apiResponse";
import PageFormModal from "./PageFormModal";

const PagesManager = () => {
  const toast = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await pagesApi.getPages(1, 100);
      setPages(data?.items || []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.slug?.toLowerCase().includes(q)
    );
  }, [pages, query]);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      if (editing) {
        await pagesApi.updatePage(editing.id, {
          ...payload,
          rowVersion: editing.rowVersion,
        });
        toast.success("Page updated.");
      } else {
        await pagesApi.createPage(payload);
        toast.success("Page created.");
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await pagesApi.deletePage(deleting.id);
      toast.success("Page deleted.");
      setDeleting(null);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout title="Pages" subtitle="Manage CMS pages and open their sections.">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search pages…"
          className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
        />
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Add page
        </button>
      </div>

      {loading ? <LoadingState label="Loading pages…" /> : null}
      {!loading && error ? <ErrorState message={error} /> : null}

      {!loading && !error && filtered.length === 0 ? (
        <EmptyState
          title="No pages found"
          description="Create a page to start building sections and content."
          action={
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
            >
              Create page
            </button>
          }
        />
      ) : null}

      {!loading && !error && filtered.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Slug</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((page) => (
                <tr key={page.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-medium text-slate-900">{page.name}</td>
                  <td className="px-4 py-3 text-slate-500">{page.slug}</td>
                  <td className="px-4 py-3">
                    <span
                      className={[
                        "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                        page.isPublished
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-600",
                      ].join(" ")}
                    >
                      {page.isPublished ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/dashboard/pages/${page.id}/sections`}
                        className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white"
                      >
                        Sections
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(page);
                          setModalOpen(true);
                        }}
                        className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(page)}
                        className="rounded-xl border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <PageFormModal
        open={modalOpen}
        initial={editing}
        busy={busy}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete page"
        description={
          deleting
            ? `Delete “${deleting.name}” and all of its sections, fields, and media records?`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </DashboardLayout>
  );
};

export default PagesManager;
